import {pdfItemFromMetadata} from './make-pdf-item';
import type {UndoableState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {EditorStarterItem} from '../../vendor/src/features/remotion-editor/editor/items/item-type';
import type {EditorStarterAsset} from '../../vendor/src/features/remotion-editor/editor/assets/assets';
import {calculateMediaDimensionsForCanvas} from '../../vendor/src/features/remotion-editor/editor/utils/dimension-utils';

export type WorkMediaAsset = Extract<EditorStarterAsset, {type:'video'|'image'|'audio'|'gif'|'webp'|'pdf'}>;
export function workMediaAssets(document: Pick<UndoableState, 'assets'|'items'>) {
  const usage = new Map<string, number>();
  for (const item of Object.values(document.items)) {
    if ('assetId' in item) usage.set(item.assetId, (usage.get(item.assetId) ?? 0) + 1);
  }
  return Object.values(document.assets)
    .filter((asset): asset is WorkMediaAsset => ['video','image','audio','gif','webp','pdf'].includes(asset.type))
    .map(asset => ({asset, placements:usage.get(asset.id) ?? 0}));
}

/** Reuse the document's source, never download/re-register it as another asset. */
export function prepareWorkAsset(document: UndoableState, assetId:string, itemId:string, from:number, dropPosition:{x:number;y:number}|null=null) {
  if (dropPosition && ![dropPosition.x,dropPosition.y].every(Number.isFinite)) throw new Error('배치할 화면 위치를 확인해 주세요.');
  const asset = Object.hasOwn(document.assets,assetId) ? document.assets[assetId] : undefined;
  if (!asset || !['video','image','audio','gif','webp','pdf'].includes(asset.type)) throw new Error('현재 작업에서 원본 에셋을 찾지 못했습니다.');
  if (!itemId || Object.hasOwn(document.items,itemId) || ['__proto__','constructor','prototype'].includes(itemId)) throw new Error('새 배치 ID가 올바르지 않습니다.');
  const media = asset as WorkMediaAsset;
  if(media.type==='pdf'){
    if(!Number.isSafeInteger(from)||from<0)throw Error('Invalid PDF start frame');
    const prepared=pdfItemFromMetadata({file:{size:media.size},filename:media.filename,remoteUrl:media.remoteUrl,remoteFileKey:media.remoteFileKey,fps:document.fps,compositionWidth:document.compositionWidth,compositionHeight:document.compositionHeight,currentFrame:from,dropPosition},{numPages:media.numPages,pageWidth:media.pageWidth,pageHeight:media.pageHeight},media.id,itemId);
    return {item:prepared.item,asset:media};
  }
  const duration = media.type === 'image' ? Math.round(document.fps * 2) : media.durationInSeconds * document.fps;
  if (!Number.isFinite(duration) || duration < 1 || !Number.isSafeInteger(from) || from < 0
    || !Number.isFinite(document.fps) || document.fps <= 0 || !Number.isSafeInteger(from + Math.floor(duration))) throw new Error('원본 재생 길이를 확인해 주세요.');
  const common = {id:itemId, assetId, from, durationInFrames:Math.floor(duration), opacity:1, isDraggingInTimeline:false};
  const sound = {decibelAdjustment:0, playbackRate:1, audioFadeInDurationInSeconds:0, audioFadeOutDurationInSeconds:0};
  let item:EditorStarterItem;
  if (media.type === 'audio') {
    item = {...common,...sound,type:'audio',top:0,left:0,width:100,height:100,audioStartFromInSeconds:0};
  } else {
    if (![media.width,media.height,document.compositionWidth,document.compositionHeight].every(n=>Number.isFinite(n)&&n>0)) throw new Error('원본 화면 크기를 확인해 주세요.');
    const dimensions = calculateMediaDimensionsForCanvas({mediaWidth:media.width,mediaHeight:media.height,
      containerWidth:document.compositionWidth,containerHeight:document.compositionHeight,dropPosition});
    const visual = {...common,...dimensions,borderRadius:0,rotation:0,keepAspectRatio:true,
      fadeInDurationInSeconds:0,fadeOutDurationInSeconds:0,cropLeft:0,cropTop:0,cropRight:0,cropBottom:0};
    item = media.type === 'image' ? {...visual,type:'image'}
      : media.type==='gif'?{...visual,type:'gif',playbackRate:1,gifStartFromInSeconds:0}
      : media.type==='webp'?{...visual,type:'webp',playbackRate:1,webpStartFromInSeconds:0}
      : {...visual,...sound,type:'video',videoStartFromInSeconds:0};
  }
  return {item,asset:media};
}

export const WORK_ASSET_DRAG_TYPE = 'application/x-waka-work-asset';
export type WorkAssetDrag = {assetId:string;document:UndoableState;name:string;duration:number;durationInFrames?:number;contentType:1|2|3};
let currentDrag:WorkAssetDrag|null=null;
export const getWorkAssetDrag = () => currentDrag;
export const setWorkAssetDrag = (drag:WorkAssetDrag|null) => {currentDrag=drag;};
